import { OPPONENT } from './constants'
import {
  getLegalMoves,
  applyMove,
  hasAnyLegalMove,
} from './board'
import { evaluate, evaluateTerminal } from './evaluation'
import gameConfig, { getAiDepth, randomBetween } from '../config/gameConfig'

function gameOver(board, size) {
  const b = hasAnyLegalMove(board, size, 1)
  const w = hasAnyLegalMove(board, size, 2)
  return !b && !w
}

function orderMoves(board, size, moves) {
  const corner = [
    [0, 0],
    [0, size - 1],
    [size - 1, 0],
    [size - 1, size - 1],
  ]
  const scoreMove = ([r, c]) => {
    for (const [cr, cc] of corner) {
      if (r === cr && c === cc) return 1000
    }
    if (r === 0 || r === size - 1 || c === 0 || c === size - 1) return 50
    if (r === 1 || r === size - 2) {
      if (c === 1 || c === size - 2) return -200
    }
    return 0
  }
  return moves.slice().sort((a, b) => scoreMove(b) - scoreMove(a))
}

function search(board, size, depth, alpha, beta, maximizingPlayer, aiColor) {
  if (gameOver(board, size)) {
    return { score: evaluateTerminal(board, size, aiColor), move: null }
  }
  if (depth === 0) {
    return { score: evaluate(board, size, aiColor), move: null }
  }

  const toMove = maximizingPlayer ? aiColor : OPPONENT[aiColor]
  let moves = getLegalMoves(board, size, toMove)
  if (moves.length === 0) {
    const passSide = maximizingPlayer ? OPPONENT[aiColor] : aiColor
    if (hasAnyLegalMove(board, size, passSide)) {
      return search(
        board,
        size,
        depth - 1,
        alpha,
        beta,
        !maximizingPlayer,
        aiColor
      )
    }
    return { score: evaluateTerminal(board, size, aiColor), move: null }
  }

  moves = orderMoves(board, size, moves)
  let bestMove = moves[0]
  let bestScore = maximizingPlayer ? -Infinity : Infinity

  for (const [r, c] of moves) {
    const next = applyMove(board, size, r, c, toMove)
    const { score } = search(
      next,
      size,
      depth - 1,
      alpha,
      beta,
      !maximizingPlayer,
      aiColor
    )

    if (maximizingPlayer) {
      if (score > bestScore) {
        bestScore = score
        bestMove = [r, c]
      }
      alpha = Math.max(alpha, bestScore)
    } else {
      if (score < bestScore) {
        bestScore = score
        bestMove = [r, c]
      }
      beta = Math.min(beta, bestScore)
    }
    if (beta <= alpha) break
  }

  return { score: bestScore, move: bestMove }
}

/**
 * 选择 AI 着法；aiColor 为 AI 执棋颜色
 */
export function chooseAiMove(board, size, aiColor) {
  const moves = getLegalMoves(board, size, aiColor)
  if (moves.length === 0) return null
  if (moves.length === 1) return moves[0]

  const maxDepth = getAiDepth(size)
  let bestMove = moves[0]

  const ordered = orderMoves(board, size, moves)

  for (let depth = 2; depth <= maxDepth; depth++) {
    let layerBest = ordered[0]
    let layerScore = -Infinity
    for (const [r, c] of ordered) {
      const next = applyMove(board, size, r, c, aiColor)
      const { score } = search(
        next,
        size,
        depth - 1,
        -Infinity,
        Infinity,
        false,
        aiColor
      )
      if (score > layerScore) {
        layerScore = score
        layerBest = [r, c]
      }
    }
    bestMove = layerBest
  }

  return bestMove
}

export function pickHumanLikeDelay(size, moveIndex, legalCount = 10) {
  const ai = gameConfig.ai
  const base = randomBetween(ai.delayBaseMin, ai.delayBaseMax)
  const think = Math.min(
    ai.delayCap,
    350 + moveIndex * ai.delayPerMove + size * ai.delaySizeFactor
  )
  const complex =
    legalCount <= 4 ? ai.delayComplexityBonus + Math.random() * 400 : 0
  return Math.floor(base + think * Math.random() + complex)
}